import useUpdateDB from './updateDB.js';
import React from 'react';
import _ from 'lodash';
import { toast } from './toast';
import { 
  columnHeaderMappings, 
  FIELD_TYPES, 
  specialFormatters,
  conditionMap,
  gradeMap
} from './mappings.js';
import logger from '../utils/logger';
import { InventoryUpdateValidationError } from './updateDB.js';
import { INVENTORY_UPDATE_VALIDATION_TOAST_OPTIONS } from '../components/CaseReview/CaseReviewHelpers/inventoryUpdateProtections.js';

const formatValue = (key, value, isOldValue = false) => {
  if (value === null || value === undefined || value === 'N/A') return 'N/A';
  
  // Handle special formatters first
  if (specialFormatters[key]) {
    if (key === 'View') {
      // View requires both waterfront and waterview values
      return value; // This will be handled in the display logic
    } else if (key === 'totalBaths') {
      return specialFormatters[key](value, 0); // Assume halfBath is 0 for display
    } else {
      return specialFormatters[key](value);
    }
  }
  
  // Get field type from mappings
  const fieldType = FIELD_TYPES[key];
  
  // Handle currency fields
  if (fieldType === 'currency') {
    const numValue = Number(value);
    if (isNaN(numValue)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  }
  
  // Handle percentage fields
  if (key === 'IFMVRatio') {
    const numValue = Number(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue * 100).toFixed(2)}%`;
  }
  
  // Handle boolean fields
  if (fieldType === 'boolean' || key === 'ALFlag') {
    if (value === 1 || value === '1' || value === true) return 'Yes';
    if (value === 0 || value === '0' || value === false) return 'No';
    return 'N/A';
  }
  
  // Handle date fields
  if (fieldType === 'date') {
    if (!value) return 'N/A';
    return new Date(value).toLocaleDateString();
  }
  
  // Handle numeric fields
  if (fieldType === 'number') {
    const numValue = Number(value);
    if (isNaN(numValue)) return 'N/A';
    
    if (isOldValue) {
      // Check if the value has more than 4 decimal places
      const decimalPlaces = (numValue.toString().split('.')[1] || '').length;
      if (decimalPlaces > 4) {
        return 'N/A - Imputed';
      }
      // Only show decimals for Acres field
      if (key === 'Acres') {
        const formatted = numValue.toFixed(3);
        return Number(formatted).toLocaleString(undefined, {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3
        });
      }
      // YearBuilt should not be formatted with locale string
      // YearBuilt should not be formatted with locale string
      if (key === 'YearBuilt') {
        return numValue.toString();
      }
      return numValue.toLocaleString();
    }
    // YearBuilt should not be formatted with locale string
    if (key === 'YearBuilt') {
      return numValue.toString();
    }
    return numValue.toLocaleString();
  }
  
  // Handle condition and grade mapping
  if (key === 'ConditionIn' && conditionMap[value]) {
    return conditionMap[value];
  }
  
  if (key === 'Grade' && gradeMap[value]) {
    return gradeMap[value];
  }
  
  // Default formatting for other fields
  const numValue = Number(value);
  if (!isNaN(numValue) && typeof value !== 'boolean') {
    if (isOldValue) {
      const decimalPlaces = (numValue.toString().split('.')[1] || '').length;
      if (decimalPlaces > 4) {
        return 'N/A - Imputed';
      }
      // Only show decimals for Acres field
      if (key === 'Acres') {
        const formatted = numValue.toFixed(3);
        return Number(formatted).toLocaleString(undefined, {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3
        });
      }
      // YearBuilt should not be formatted with locale string
      if (key === 'YearBuilt') {
        return numValue.toString();
      }
      return numValue.toLocaleString();
    }
    // YearBuilt should not be formatted with locale string
    if (key === 'YearBuilt') {
      return numValue.toString();
    }
    return numValue.toLocaleString();
  }
  
  return value;
};

// Helper function to handle special composite fields like View and Pool
const formatSpecialField = (key, beforeRow, afterRow) => {
  if (key === 'View') {
    const beforeView = beforeRow.View || 'None';
    const afterView = afterRow.View || 'None';
    return { before: beforeView, after: afterView };
  }
  
  if (key === 'Pool') {
    const beforePool = beforeRow.Pool || 'None';
    const afterPool = afterRow.Pool || 'None';
    return { before: beforePool, after: afterPool };
  }
  
  return null;
};

const DBConfirmModal = ({ 
  inventoryUpdates, 
  confirmModal, 
  handleConfirmCancel, 
  // handleCloseModal,
  onDBUpdateSuccess // Pass the callback from the parent
}) => {
  const { updateDB } = useUpdateDB();

  if (!confirmModal) {
    return null; // Render nothing if confirmModal is false
  }

  const originalSubject = inventoryUpdates.originalSubject;
  const unsavedRows = inventoryUpdates.unsavedRows || [];
  const rowsBeforeChange = inventoryUpdates.rowsBeforeChange || {};

  const before = Object.keys(originalSubject).length > 0 
    ? [originalSubject, ...Object.values(rowsBeforeChange)] 
    : [...Object.values(rowsBeforeChange)];

  const after = Object.keys(inventoryUpdates.unsavedSubject).length > 0 
    ? [inventoryUpdates.unsavedSubject, ...Object.values(unsavedRows)] 
    : [...Object.values(unsavedRows)];

    const afterBackend = _.cloneDeep(after);
  

  const handleConfirm = () => {
    logger.log('Before updateDB call - afterBackend:', afterBackend);
    logger.log('Original subject:', originalSubject);
    logger.log('Unsaved rows:', unsavedRows);
    logger.log('Rows before change:', rowsBeforeChange);
    
    updateDB({ confirmed: true, handleConfirmCancel, updatedValues: afterBackend })
      .then((response) => {
        logger.log('Response from updateDB:', response);
        // Will this work if it is a stream instead of a regular response?
        // Pass the response to the parent component
        onDBUpdateSuccess(response);
        handleConfirmCancel({}, true); // Close the modal
      })
      .catch((error) => {
        logger.error('Error while updating DB:', error);

        if (error instanceof InventoryUpdateValidationError) {
          toast.error(error.message, INVENTORY_UPDATE_VALIDATION_TOAST_OPTIONS);
          handleConfirmCancel();
          return;
        }

        const errorMessage = error.response?.data?.error || error.message || 'Error updating database';
        toast.error(errorMessage, { position: 'top-right', autoClose: 5000 });
      });
  };

  return (
    <div className="fixed inset-0 p-4 flex justify-center items-center w-full h-full z-[999999] bg-gray-600 bg-opacity-50">
      <div className="bg-white p-6 rounded-md shadow-lg max-w-2xl w-full">
        <h2 className="text-xl font-semibold mb-4">Review Changes</h2>
        <div className="w-full">
          <div className="flex font-bold border-b-2 pb-2">
            <div className="w-1/4 pr-4">Address</div>
            <div className="w-1/4 pr-4">Field</div>
            <div className="w-1/4 pr-4">New Value</div>
            <div className="w-1/4">Old Value</div>
          </div>
          {before.map((beforeRow, index) => {
            const afterRow = after[index];
            if (!afterRow) return null; // Prevents crash if afterRow is undefined
            return (
              <div key={index} className="flex border-b py-2">
                <div className="w-1/4 pr-4">{beforeRow.PropertyInfo?.Address || 'N/A'}</div>
                <div className="w-3/4">
                  {Object.keys(beforeRow).map((key) => {
                    const beforeValue = beforeRow[key];
                    const afterValue = afterRow ? afterRow[key] : undefined;
                    
                    // Only show fields that have actually changed
                    if (afterValue === undefined) return null;
                    
                    // Handle special composite fields (View and Pool)
                    if (key === 'View' || key === 'Pool') {
                      const specialField = formatSpecialField(key, beforeRow, afterRow);
                      if (specialField && specialField.before !== specialField.after) {
                        return (
                          <div key={key} className="flex">
                            <div className="w-1/3 pr-4">
                              <span className="font-medium">{columnHeaderMappings[key] || key}</span>
                            </div>
                            <div className="w-1/3 pr-4">
                              {specialField.after}
                            </div>
                            <div className="w-1/3 text-gray-500">
                              {specialField.before}
                            </div>
                          </div>
                        );
                      }
                      return null; // Don't show if no change
                    }
                    
                    // Handle regular fields - only show if values are different
                    if (beforeValue !== afterValue) {
                      return (
                        <div key={key} className="flex">
                          <div className="w-1/3 pr-4">
                            <span className="font-medium">{columnHeaderMappings[key] || key}</span>
                          </div>
                          <div className="w-1/3 pr-4">
                            {formatValue(key, afterValue)}
                          </div>
                          <div className="w-1/3 text-gray-500">
                            {formatValue(key, beforeValue, true)}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={handleConfirmCancel}
            className="px-4 py-2 mr-2 bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 bg-green-500 text-white rounded-md"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default DBConfirmModal;
